import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { centralTzLabel } from '../lib/time';
import api from '../lib/api';
import PinnedNotesSidebar from './PinnedNotesSidebar';
import QuickTab from './QuickTab';
import BuildBadge from './BuildBadge';
import {
  LayoutGrid,
  Radar,
  Briefcase,
  FlaskConical,
  BellRing,
  FileText,
  Shield,
  Settings,
  Power,
  Radio,
  Brain,
  Activity,
  Gauge,
  Newspaper,
  Archive,
  StickyNote,
  Globe as GlobeIcon,
  Trophy,
  Menu,
  X as XIcon,
  FlaskRound,
  Bitcoin,
  UserCog,
  Bot,
} from 'lucide-react';

const nav = [
  { to: '/app/dashboard',  label: 'COMMAND',    icon: LayoutGrid,   id: 'nav-dashboard' },
  { to: '/app/discovery',  label: 'DISCOVERY',  icon: Radar,        id: 'nav-discovery' },
  { to: '/app/portfolio',  label: 'PORTFOLIO',  icon: Briefcase,    id: 'nav-portfolio' },
  { to: '/app/sim',        label: 'LIVE SIM',   icon: Activity,     id: 'nav-sim'       },
  { to: '/app/truth',      label: 'TRUTH',      icon: Gauge,        id: 'nav-truth'     },
  { to: '/app/news',       label: 'NEWS',       icon: Newspaper,    id: 'nav-news'      },
  { to: '/app/globe',      label: 'GLOBE',      icon: GlobeIcon,    id: 'nav-globe'     },
  // Phase 48 — AltMarkets removed from product surface (Trading Engine only).
  { to: '/app/backtest',   label: 'BACKTEST',   icon: FlaskConical, id: 'nav-backtest'  },
  { to: '/app/learning',   label: 'LEARNING',   icon: Brain,        id: 'nav-learning'  },
  { to: '/app/notes',      label: 'NOTES',      icon: StickyNote,   id: 'nav-notes'     },
  { to: '/app/alerts',     label: 'ALERTS',     icon: BellRing,     id: 'nav-alerts'    },
  { to: '/app/log',        label: 'LOG',        icon: Archive,      id: 'nav-log'       },
  { to: '/app/manual',     label: 'MANUAL',     icon: FileText,     id: 'nav-manual'    },
  { to: '/app/audit',      label: 'AUDIT',      icon: Shield,       id: 'nav-audit'     },
  { to: '/app/settings',   label: 'SETTINGS',   icon: Settings,     id: 'nav-settings'  },
  { to: '/app/access',     label: 'ACCESS',     icon: UserCog,      id: 'nav-access',   adminOnly: true },
  { to: '/app/auto-trading', label: 'AUTO TRADING', icon: Bot,       id: 'nav-auto-trading', adminOnly: true },
  { to: '/app/lab',        label: 'LAB',        icon: FlaskRound,   id: 'nav-lab'       },
  { to: '/app/crypto',     label: 'CRYPTO',     icon: Bitcoin,      id: 'nav-crypto'    },
];

export default function AppShell({ children }) {
  const { user, role, logout } = useAuth();
  const nav_ = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Live clock — military time (UTC, 24h) → CST translation, updated every 1s.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  const utcMilitary = now.toISOString().slice(11, 19); // HH:MM:SS UTC
  const cstMilitary = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Chicago',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(now);
  // CST during winter / CDT during DST — auto-detected from the real instant
  const ctLabel = centralTzLabel(now);

  // P12C: System mode polling — surfaces the degraded-mode banner when
  // finnhub breaker is open. Poll every 20s (cheap endpoint).
  const [sysMode, setSysMode] = useState({ mode: 'normal' });
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get('/system/mode');
        if (!cancelled) setSysMode(data || { mode: 'normal' });
      } catch {}
    };
    check();
    const iv = setInterval(check, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Unread-alert count — polled every 30s to drive the blinking nav dot.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get('/alerts/unread-count');
        if (!cancelled) setUnread(data.unread || 0);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // P12B Ping Reliability Engine:
  //  • dedicated worker separate from UI re-renders
  //  • never shows "-" — rolling avg fallback kicks in immediately
  //  • "reconnecting..." only after 30s offline
  const [ping, setPing] = useState({ ms: null, state: 'unknown', label: '—' });
  const pingHistoryRef = useRef([]);    // last 6 successful ms
  const lastOkAtRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const HEALTH_URL = `${process.env.REACT_APP_BACKEND_URL}/api/health`;

    const rollingAvg = () => {
      const h = pingHistoryRef.current;
      if (!h.length) return null;
      return Math.round(h.reduce((a, b) => a + b, 0) / h.length);
    };

    const pingOnce = async () => {
      const start = performance.now();
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 4000);
        const r = await fetch(HEALTH_URL, {
          cache: 'no-store', signal: ctrl.signal,
          headers: { Accept: 'application/json' },
        });
        clearTimeout(t);
        const ms = Math.round(performance.now() - start);
        if (cancelled) return;
        if (!r.ok) throw new Error('bad-status');
        // Success — update history + lastOk.
        pingHistoryRef.current.push(ms);
        if (pingHistoryRef.current.length > 6) pingHistoryRef.current.shift();
        lastOkAtRef.current = Date.now();
        // Phase 14 §9C: smooth ping by averaging last 3 samples so the
        // display stops flickering on single-spike network blips.
        const recent = pingHistoryRef.current.slice(-3);
        const smoothed = Math.round(recent.reduce((a, b) => a + b, 0) / recent.length);
        const state = smoothed < 100 ? 'fast' : smoothed < 500 ? 'ok' : 'slow';
        setPing({ ms: smoothed, state, label: `${smoothed}ms` });
      } catch {
        if (cancelled) return;
        // Fallback chain:
        //   1. rolling avg if we have recent data and offline < 30s
        //   2. otherwise reconnecting...
        const offlineFor = Date.now() - lastOkAtRef.current;
        const avg = rollingAvg();
        if (avg != null && offlineFor < 30000) {
          setPing({ ms: avg, state: 'slow', label: `~${avg}ms` });
        } else {
          setPing({ ms: null, state: 'offline', label: 'reconnecting...' });
        }
      }
    };
    pingOnce();
    const iv = setInterval(pingOnce, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  const pingColor =
    ping.state === 'fast'    ? 'text-buy' :
    ping.state === 'ok'      ? 'text-cyan-sys' :
    ping.state === 'slow'    ? 'text-hold' :
    ping.state === 'offline' ? 'text-sell' : 'text-[var(--txt-muted)]';

  return (
    <div className="min-h-screen md:grid md:grid-cols-[220px_1fr]">
      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/70 md:hidden"
          data-testid="mobile-nav-backdrop"
        />
      )}
      {/* SIDEBAR — sticky to viewport so operator bar stays visible while
          the main content scrolls. `align-self: start` keeps it pinned to
          the top of its grid cell, and `height: 100vh + overflow-y:auto`
          lets the sidebar scroll internally if the nav ever exceeds the
          viewport height. */}
      <aside
        className={`border-r border-[var(--border-default)] bg-[var(--bg-panel)] flex flex-col
          fixed md:sticky top-0 left-0 z-40 w-[220px]
          transition-transform duration-200 md:translate-x-0
          ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          md:self-start`}
        style={{ height: '100vh', overflowY: 'auto' }}
      >
        <div className="px-4 py-5 border-b border-[var(--border-default)]">
          <div className="font-display font-bold text-xl tracking-[0.35em] text-cyan-sys glow-cyan">ATDE</div>
          <div className="text-[10px] text-[var(--txt-secondary)] tracking-[0.2em] mt-1 uppercase">
            AI TRADING DECISION ENGINE
          </div>
          <div className="flex items-center gap-2 mt-3 text-[10px]">
            <Radio size={10} className="text-buy animate-pulse" />
            <span className="text-buy">SYSTEM ONLINE</span>
          </div>
        </div>
        <nav className="flex-1 py-2">
          {nav.filter((item) => !item.adminOnly || role === 'admin').map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.id}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-[12px] font-display tracking-[0.2em] uppercase transition-colors
                 ${isActive
                   ? 'text-cyan-sys bg-[#0e1214] border-l-2 border-cyan-sys'
                   : 'text-[var(--txt-secondary)] hover:text-[var(--txt-primary)] border-l-2 border-transparent'}`
              }
            >
              <item.icon size={14} />
              <span className="flex-1">{item.label}</span>
              {item.id === 'nav-alerts' && unread > 0 && (
                <span
                  data-testid="nav-alerts-badge"
                  title={`${unread} unread alert${unread > 1 ? 's' : ''}`}
                  className="relative inline-flex items-center justify-center min-w-[18px] h-4 px-1 bg-sell text-black text-[9px] font-mono tabular rounded-sm"
                >
                  <span className="absolute -left-0.5 -top-0.5 block h-2 w-2 rounded-full bg-sell animate-ping" />
                  <span className="relative">{unread > 99 ? '99+' : unread}</span>
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[var(--border-default)] p-3 text-[11px]">
          <div className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">operator</div>
          <div className="text-cyan-sys font-mono">{user || '—'}</div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); nav_('/login'); }}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-sell text-sell py-1.5 hover:bg-sell hover:text-black transition-colors text-[11px] uppercase tracking-[0.2em]"
          >
            <Power size={12} /> Sign Out
          </button>
          <div
            data-testid="sidebar-clock"
            className="mt-3 pt-3 border-t border-[var(--border-default)] text-[9px] font-mono text-[var(--txt-secondary)] tracking-widest space-y-0.5"
          >
            <div className="flex justify-between">
              <span>UTC</span>
              <span className="tabular live-num">{utcMilitary}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-cyan-sys">{ctLabel}</span>
              <span className="tabular live-num text-cyan-sys">{cstMilitary}</span>
            </div>
            <div
              data-testid="sidebar-ping"
              title={`backend health: ${ping.state}${ping.ms != null ? ' · '+ping.ms+'ms' : ''}`}
              className="flex justify-between pt-0.5 border-t border-[var(--border-default)] mt-0.5"
            >
              <span className={pingColor}>PING</span>
              <span className={`tabular live-num ${pingColor}`}>
                {ping.label}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="min-w-0">
        <div
          className="h-10 border-b border-[var(--border-default)] px-2 md:px-4 flex items-center justify-between bg-[var(--bg-panel)] sticky top-0 z-20"
        >
          <div className="flex items-center gap-2 md:gap-4 text-[10px] font-mono text-[var(--txt-secondary)] tracking-widest">
            <button
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden text-cyan-sys p-1 border border-[var(--border-muted)]"
              aria-label="Open menu"
            >
              <Menu size={14} />
            </button>
            <span className="text-cyan-sys">// ATDE.v1</span>
            <span data-testid="top-session-clock" className="tabular live-num hidden sm:inline">
              SESSION {cstMilitary} {ctLabel}
            </span>
            <span className="hidden lg:inline">CLEARANCE: OPERATOR</span>
          </div>
          <div className="flex items-center gap-3">
            {sysMode.mode === 'degraded' && (
              <span
                data-testid="degraded-mode-banner"
                className="text-[10px] tracking-[0.3em] uppercase px-2 py-0.5 border border-hold text-hold animate-pulse"
                title={`Finnhub breaker open · cooldown ${sysMode?.breaker?.cooldown_s ?? '?'}s · serving from cache`}
              >
                ⚠ DEGRADED MODE · STABILIZING {Math.ceil(sysMode?.breaker?.cooldown_s || 0)}s
              </span>
            )}
            <div className="text-[10px] text-[var(--txt-secondary)] tracking-widest hidden sm:block">
              FIREWALL <span className="text-buy">● ACTIVE</span>
            </div>
            <BuildBadge />
          </div>
        </div>
        <div className="boot-in">{children}</div>
      </main>
      {/* P12: Pinned notes slide-over — globally accessible from any tab */}
      <PinnedNotesSidebar />
      {/* Phase 15 §3B: Quick Tab — unified assist + notes overlay */}
      <QuickTab />
    </div>
  );
}
